/** @format */
import React, { FunctionComponent, useContext, useMemo } from 'react';

import { MarketContext } from '@toucan/client/global/market/context';

import { getModelIdFromQuery } from '@toucan/client/features/search/helpers';
import { usePriceModifierGroups } from '@toucan/client/features/search/hooks/use-model-price-modifier-groups';

import { ExtraAccessoryFilter } from './components/extra-accessory-filter';
import { IncludedAccessoryFilter } from './components/included-accessory-filter';
import { SameAccessoriesNotice } from './components/same-accessories-notice';
import { TEST_ID as dataTestId } from './constants';
import {
    areAllAccessoryOptionsCountEqualToInitalResultCount,
    getSelectedOptionsByModifierGroupType,
} from './helpers';
import { AccessoryFiltersProps } from './types';

export const AccessoryFilters: FunctionComponent<AccessoryFiltersProps> = ({
    filter,
    onAccessorySelect,
    totalInitialResults,
    showIcons = true,
    isGroupNameFullWidthStyle = false,
}) => {
    const { activeMarket } = useContext(MarketContext);

    const modelId = getModelIdFromQuery(filter.baseFilterQuery);

    const { data: modifierGroups } = usePriceModifierGroups(
        modelId,
        activeMarket.code
    );

    const essentialModifierGroups = useMemo(
        () => modifierGroups?.essential ?? [],
        [modifierGroups?.essential]
    );

    const extraModifierGroups = useMemo(
        () => modifierGroups?.extra ?? [],
        [modifierGroups?.extra]
    );

    const hasSameEssentialAccessories =
        areAllAccessoryOptionsCountEqualToInitalResultCount(
            essentialModifierGroups,
            filter,
            totalInitialResults
        );

    const hasSameExtraAccessories =
        areAllAccessoryOptionsCountEqualToInitalResultCount(
            extraModifierGroups,
            filter,
            totalInitialResults
        );
    const allProductsIncludeSameAccessories =
        hasSameEssentialAccessories && hasSameExtraAccessories;

    const { selectedEssentialOptions, selectedExtraOptions } = useMemo(
        () =>
            getSelectedOptionsByModifierGroupType(
                essentialModifierGroups,
                extraModifierGroups,
                filter.selectedOptions
            ),
        [filter.selectedOptions, essentialModifierGroups, extraModifierGroups]
    );

    const includedFilter = useMemo(
        () => ({
            ...filter,
            selectedOptions: selectedEssentialOptions,
        }),
        [filter, selectedEssentialOptions]
    );

    const extraFilter = useMemo(
        () => ({
            ...filter,
            selectedOptions: selectedExtraOptions,
        }),
        [filter, selectedExtraOptions]
    );

    return (
        <div data-testid={dataTestId}>
            {allProductsIncludeSameAccessories && <SameAccessoriesNotice />}

            {!hasSameEssentialAccessories && (
                <IncludedAccessoryFilter
                    filter={includedFilter}
                    onAccessorySelect={(newlySelectedOptions) => {
                        onAccessorySelect([
                            ...newlySelectedOptions,
                            ...selectedExtraOptions,
                        ]);
                    }}
                    showIcon={showIcons}
                    isGroupNameFullWidthStyle={isGroupNameFullWidthStyle}
                />
            )}
            {!hasSameExtraAccessories && (
                <ExtraAccessoryFilter
                    filter={extraFilter}
                    onAccessorySelect={(newlySelectedOptions) => {
                        onAccessorySelect([
                            ...newlySelectedOptions,
                            ...selectedEssentialOptions,
                        ]);
                    }}
                    showIcon={showIcons}
                />
            )}
        </div>
    );
};
